import { useState } from 'react';
import { FileText, Search, Camera, Sparkles, AlertCircle, CheckCircle2, X, Download, ArrowRight } from 'lucide-react';
import ReceiptSnap from '../client-pwa/ReceiptSnap';

type FileItem = {
  id: string;
  name: string;
  category: 'Tax Return' | 'BAS' | 'P&L' | 'Letters';
  date: string;
  size: string;
  year: string;
};

const files: FileItem[] = [
  { id: '1', name: 'Tax Return FY2024-25.pdf', category: 'Tax Return', date: '12 Apr 2026', size: '2.4 MB', year: '2026' },
  { id: '2', name: 'Profit & Loss — Mar 2026.pdf', category: 'P&L', date: '8 Apr 2026', size: '480 KB', year: '2026' },
  { id: '3', name: 'BAS Q3 2026.pdf', category: 'BAS', date: '5 Apr 2026', size: '320 KB', year: '2026' },
  { id: '4', name: 'Letter of Income.pdf', category: 'Letters', date: '28 Mar 2026', size: '180 KB', year: '2026' },
  { id: '5', name: 'Profit & Loss — Feb 2026.pdf', category: 'P&L', date: '4 Mar 2026', size: '472 KB', year: '2026' },
  { id: '6', name: 'BAS Q2 2026.pdf', category: 'BAS', date: '28 Jan 2026', size: '316 KB', year: '2026' },
  { id: '7', name: 'Tax Return FY2023-24.pdf', category: 'Tax Return', date: '15 Aug 2025', size: '2.1 MB', year: '2025' },
  { id: '8', name: 'Profit & Loss — FY2023-24.pdf', category: 'P&L', date: '14 Aug 2025', size: '520 KB', year: '2025' },
];

const requested = [
  { id: 'r1', title: 'Q4 bank statement', detail: 'CommBank business · Jan–Mar', due: 'Due 30 Apr', status: 'pending' as const },
  { id: 'r2', title: 'Vehicle logbook', detail: '12 weeks ending March', due: 'Due 5 May', status: 'pending' as const },
  { id: 'r3', title: 'Equipment receipts', detail: 'Asset purchases over $1,000', due: 'Received 8 Apr', status: 'uploaded' as const },
];

export default function FilesPageV3() {
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<string>('All');
  const [previewFile, setPreviewFile] = useState<FileItem | null>(null);
  const [receiptOpen, setReceiptOpen] = useState(false);

  const categories = ['All', 'Tax Return', 'BAS', 'P&L', 'Letters'];
  const filtered = files.filter(f => {
    const matchSearch = f.name.toLowerCase().includes(search.toLowerCase());
    const matchCat = filter === 'All' || f.category === filter;
    return matchSearch && matchCat;
  });

  const grouped = filtered.reduce<Record<string, FileItem[]>>((acc, f) => {
    if (!acc[f.year]) acc[f.year] = [];
    acc[f.year].push(f);
    return acc;
  }, {});

  return (
    <div className="space-y-6 pt-6">
      {/* Editorial header */}
      <div>
        <p className="font-mono text-[12px] font-bold uppercase tracking-[0.25em] text-stone-500">§ 02 · Records</p>
        <h2 className="mt-2 font-serif text-3xl font-bold leading-tight tracking-tight">The Archive</h2>
        <p className="mt-1 text-[12px] uppercase tracking-wider text-stone-500">{files.length} documents · last updated 12 Apr 2026</p>
      </div>

      {/* Quick capture */}
      <button
        onClick={() => setReceiptOpen(true)}
        className="group flex w-full items-center gap-4 border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-900 px-4 py-3.5 text-left transition-colors hover:border-teal-700"
      >
        <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center border border-teal-700 dark:border-teal-500 bg-teal-700 dark:bg-teal-500 text-white">
          <Camera className="h-4 w-4" />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-1.5">
            <p className="text-sm font-semibold text-stone-900 dark:text-stone-100">Photograph a receipt</p>
            <span className="inline-flex items-center gap-0.5 border border-teal-700/30 dark:border-teal-500/30 bg-teal-50 px-1.5 py-0.5 font-mono text-[10px] font-bold uppercase text-teal-700 dark:text-teal-400">
              <Sparkles className="h-2 w-2" /> AI
            </span>
          </div>
          <p className="text-[13px] text-stone-500">Vendor, amount and GST extracted automatically</p>
        </div>
        <ArrowRight className="h-4 w-4 text-stone-400 dark:text-stone-500 transition-transform group-hover:translate-x-1" />
      </button>

      {/* Awaiting */}
      <section>
        <div className="border-l-4 border-amber-700 bg-amber-50/40 dark:bg-amber-950/30 px-4 py-3">
          <p className="font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-amber-800 dark:text-amber-300">Awaiting Your Submission</p>
        </div>
        <div className="divide-y divide-stone-200 dark:divide-stone-800 border-x border-b border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900">
          {requested.map(r => (
            <div key={r.id} className="flex items-center gap-3 px-4 py-3">
              <div className={`flex h-8 w-8 flex-shrink-0 items-center justify-center border ${
                r.status === 'uploaded'
                  ? 'border-emerald-700 bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-400'
                  : 'border-amber-700 bg-white dark:bg-stone-900 text-amber-700 dark:text-amber-400'
              }`}>
                {r.status === 'uploaded' ? <CheckCircle2 className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-stone-900 dark:text-stone-100">{r.title}</p>
                <p className="truncate text-[13px] text-stone-500">{r.detail}</p>
                <p className={`mt-0.5 font-mono text-[11px] font-bold uppercase tracking-wider ${
                  r.status === 'uploaded' ? 'text-emerald-700 dark:text-emerald-400' : 'text-amber-700 dark:text-amber-400'
                }`}>
                  {r.due}
                </p>
              </div>
              {r.status === 'pending' && (
                <button className="flex-shrink-0 border border-stone-900 dark:border-stone-100 bg-stone-900 dark:bg-stone-100 px-3 py-1.5 text-[13px] font-bold uppercase tracking-wider text-stone-50 hover:bg-stone-800">
                  Upload
                </button>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Search + filter */}
      <div className="space-y-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-400 dark:text-stone-500" />
          <input
            type="text"
            placeholder="Search the archive…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-900 py-2.5 pl-10 pr-4 text-sm placeholder:text-stone-400 focus:border-teal-700 focus:outline-none"
          />
        </div>

        <div className="-mx-1 flex gap-0.5 overflow-x-auto px-1 pb-1">
          {categories.map(c => (
            <button
              key={c}
              onClick={() => setFilter(c)}
              className={`flex-shrink-0 border-b-2 px-3 py-1.5 font-mono text-[12px] font-bold uppercase tracking-[0.15em] transition-colors ${
                filter === c
                  ? 'border-teal-700 dark:border-teal-500 text-stone-900 dark:text-stone-100'
                  : 'border-stone-200 dark:border-stone-800 text-stone-500 hover:text-stone-800'
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      </div>

      {/* Year-grouped table */}
      {Object.keys(grouped).length === 0 ? (
        <div className="border border-dashed border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-900 py-12 text-center">
          <FileText className="mx-auto h-8 w-8 text-stone-300 dark:text-stone-600" />
          <p className="mt-3 font-serif text-sm italic text-stone-600 dark:text-stone-400">No records found</p>
        </div>
      ) : (
        Object.entries(grouped)
          .sort((a, b) => parseInt(b[0]) - parseInt(a[0]))
          .map(([year, items]) => (
            <section key={year}>
              <div className="flex items-baseline justify-between border-b-2 border-stone-900 dark:border-stone-100 pb-2">
                <h3 className="font-serif text-2xl font-bold tabular-nums tracking-tight">FY {year}</h3>
                <p className="font-mono text-[12px] uppercase tracking-wider text-stone-500">{items.length} records</p>
              </div>
              <div className="divide-y divide-stone-200 dark:divide-stone-800">
                {items.map(file => (
                  <button
                    key={file.id}
                    onClick={() => setPreviewFile(file)}
                    className="group flex w-full items-center gap-3 py-3 text-left transition-colors hover:bg-stone-100/50"
                  >
                    <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-900">
                      <FileText className="h-4 w-4 text-stone-700 dark:text-stone-300" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-baseline gap-2">
                        <span className="font-mono text-[11px] font-bold uppercase tracking-wider text-teal-700 dark:text-teal-400">
                          {file.category}
                        </span>
                        <span className="text-[11px] text-stone-300 dark:text-stone-600">·</span>
                        <span className="font-mono text-[11px] uppercase tracking-wider text-stone-500">{file.date}</span>
                      </div>
                      <p className="mt-0.5 truncate text-sm font-semibold text-stone-900 dark:text-stone-100">{file.name}</p>
                    </div>
                    <span className="flex-shrink-0 font-mono text-[12px] tabular-nums text-stone-400 dark:text-stone-500">{file.size}</span>
                    <ArrowRight className="h-4 w-4 flex-shrink-0 text-stone-300 dark:text-stone-600 group-hover:text-stone-700" />
                  </button>
                ))}
              </div>
            </section>
          ))
      )}

      <ReceiptSnap open={receiptOpen} onClose={() => setReceiptOpen(false)} />

      {previewFile && (
        <div className="fixed inset-0 z-50 bg-stone-900/40 backdrop-blur-sm">
          <div className="absolute inset-x-0 bottom-0 top-12 mx-auto w-full max-w-[680px] overflow-hidden border border-stone-300 dark:border-stone-700 bg-stone-50 dark:bg-stone-950">
            <div className="flex items-center justify-between border-b border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900 px-5 py-4">
              <div className="min-w-0 flex-1">
                <p className="font-mono text-[11px] font-bold uppercase tracking-wider text-teal-700 dark:text-teal-400">{previewFile.category}</p>
                <p className="truncate font-serif text-base font-bold tracking-tight">{previewFile.name}</p>
                <p className="font-mono text-[12px] uppercase tracking-wider text-stone-500">{previewFile.date} · {previewFile.size}</p>
              </div>
              <button onClick={() => setPreviewFile(null)} className="flex h-8 w-8 items-center justify-center border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-900 text-stone-700 dark:text-stone-300 hover:border-stone-500">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="flex h-[calc(100%-160px)] items-center justify-center bg-stone-100 dark:bg-stone-800 p-8">
              <div className="text-center">
                <FileText className="mx-auto h-16 w-16 text-stone-300 dark:text-stone-600" />
                <p className="mt-4 font-serif text-sm italic text-stone-500">PDF preview</p>
              </div>
            </div>
            <div className="border-t border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900 p-4">
              <button className="flex w-full items-center justify-center gap-2 border border-stone-900 dark:border-stone-100 bg-stone-900 dark:bg-stone-100 py-3 text-sm font-bold uppercase tracking-wider text-stone-50 hover:bg-stone-800">
                <Download className="h-4 w-4" /> Download
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
